// Laporan Tunggakan (Prompt F) — tunggakan per pelajar dengan bucket umur:
// 1–30 / 31–60 / 61–90 / 90+ hari dari due_date. Export CSV, cetak.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Printer, Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { formatRM } from '../../../lib/format';
import { paidByInvoice, invoiceOutstanding } from '../../../lib/fees';
import { toCsv, downloadCsv, printLaporan, agingBucket } from '../../../lib/laporan';
import PageHeader from '../../../components/PageHeader';
import { CardSkeleton } from '../../../components/Skeletons';
import EmptyState from '../../../components/EmptyState';
import DataTable from '../../../components/DataTable';
import { Button } from '../../../components/ui/button';

const BUCKETS = ['1-30', '31-60', '61-90', '90+'];

export default function LaporanTunggakan() {
  const { t } = useTranslation();
  const [kelasId, setKelasId] = useState('semua');
  const [classes, setClasses] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: students }, { data: cls }, { data: invoices }, { data: pays }] = await Promise.all([
      supabase.from('students').select('id, nama, class_id').eq('aktif', true),
      supabase.from('classes').select('id, nama'),
      supabase.from('invoices').select('id, student_id, jumlah_bayar_rm, due_date'),
      supabase.from('payments').select('invoice_id, jumlah_rm').eq('voided', false),
    ]);
    const paidMap = paidByInvoice(pays || []);
    const cMap = Object.fromEntries((cls || []).map((c) => [c.id, c.nama]));
    const invByS = {};
    (invoices || []).forEach((inv) => { (invByS[inv.student_id] = invByS[inv.student_id] || []).push(inv); });
    const out = [];
    (students || []).forEach((s) => {
      if (kelasId !== 'semua' && s.class_id !== kelasId) return;
      const invs = invByS[s.id] || [];
      let tung = 0; let oldestDue = null;
      invs.forEach((inv) => {
        const baki = invoiceOutstanding(inv, paidMap);
        if (baki > 0) {
          tung += baki;
          if (inv.due_date && (!oldestDue || inv.due_date < oldestDue)) oldestDue = inv.due_date;
        }
      });
      if (tung > 0) out.push({ id: s.id, nama: s.nama, kelas: cMap[s.class_id] || '-', tunggakan: tung, bucket: agingBucket(oldestDue), due: oldestDue });
    });
    out.sort((a, b) => b.tunggakan - a.tunggakan);
    setClasses(cls || []);
    setRows(out);
    setLoading(false);
  }, [kelasId]);

  useEffect(() => { load(); }, [load]);
  const total = useMemo(() => rows.reduce((a, r) => a + r.tunggakan, 0), [rows]);
  const byBucket = useMemo(() => {
    const m = {}; BUCKETS.forEach((b) => { m[b] = 0; });
    rows.forEach((r) => { m[r.bucket] += r.tunggakan; });
    return m;
  }, [rows]);

  const doCsv = () => downloadCsv('laporan-tunggakan.csv', toCsv(rows, [
    { label: 'Pelajar', get: (r) => r.nama },
    { label: 'Kelas', get: (r) => r.kelas },
    { label: 'Tunggakan (RM)', get: (r) => r.tunggakan.toFixed(2) },
    { label: 'Umur (hari)', get: (r) => r.bucket },
    { label: 'Due date tertua', get: (r) => r.due || '' },
  ]));

  const inputCls = 'h-10 px-3 rounded-xl border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand';
  const bucketColor = { '1-30': 'bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800', '31-60': 'bg-orange-50 dark:bg-orange-950 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800', '61-90': 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800', '90+': 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700' };

  return (
    <div>
      <div className="print:hidden">
        <PageHeader title={t('lapTunggakan')} subtitle={t('lapTunggakanSub')} breadcrumbs={[{ label: t('laporan') }, { label: t('lapTunggakan') }]} />
      </div>
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-black">{t('lapTunggakan')}</h1>
        <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString('ms-MY', { dateStyle: 'long' })}</p>
      </div>

      <div className="print:hidden mb-4 flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-muted-foreground">{t('kelas')}
          <select value={kelasId} onChange={(e) => setKelasId(e.target.value)} className={`${inputCls} ml-2`}>
            <option value="semua">{t('semua')}</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.nama}</option>)}
          </select>
        </label>
        <Button onClick={load} className="h-10 bg-brand text-white hover:bg-brand-royal">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('tapis')}</Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={doCsv} className="h-10"><Download className="h-4 w-4 mr-1.5" />CSV</Button>
          <Button variant="outline" onClick={() => printLaporan(t('lapTunggakan'))} className="h-10"><Printer className="h-4 w-4 mr-1.5" />{t('cetak')}</Button>
        </div>
      </div>

      {loading ? <CardSkeleton count={3} /> : rows.length === 0 ? (
        <EmptyState icon={AlertTriangle} title={t('tiadaTunggakan')} description={t('tiadaTunggakanDesc')} />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('jumlahTunggakan')}</p>
              <p className="text-xl sm:text-2xl font-black font-mono tabular text-red-600 dark:text-red-400">{formatRM(total)}</p>
            </div>
            {BUCKETS.map((b) => (
              <div key={b} className="rounded-2xl border border-border bg-card p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{b} {t('hari')}</p>
                <p className="text-xl sm:text-2xl font-black font-mono tabular text-foreground">{formatRM(byBucket[b])}</p>
              </div>
            ))}
          </div>
          <DataTable
            testid="tunggakan"
            rows={rows}
            searchKeys={[(r) => r.nama, (r) => r.kelas]}
            statusOptions={BUCKETS.map((b) => ({ key: b, label: `${b} ${t('hari')}`, filter: (r) => r.bucket === b }))}
            columns={[
              { key: 'nama', header: t('pelajar'), sortable: true, sortValue: (r) => r.nama, render: (r) => <span className="block truncate font-medium text-foreground">{r.nama}</span> },
              { key: 'kelas', header: t('kelas'), sortable: true, sortValue: (r) => r.kelas, render: (r) => r.kelas },
              { key: 'bucket', header: t('umurTunggakan'), sortable: true, sortValue: (r) => r.bucket,
                render: (r) => <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold border ${bucketColor[r.bucket]}`}>{r.bucket} {t('hari')}</span> },
              { key: 'tunggakan', header: t('jumlah'), align: 'right', sortable: true, sortValue: (r) => r.tunggakan,
                render: (r) => <span className="font-mono font-bold tabular text-red-600 dark:text-red-400">{formatRM(r.tunggakan)}</span> },
            ]}
            tfoot={(
              <tr className="bg-muted font-bold">
                <td colSpan={3} className="px-5 py-3 text-right">{t('jumlahTunggakan')}</td>
                <td className="px-5 py-3 font-mono tabular text-right">{formatRM(total)}</td>
              </tr>
            )}
            empty={{ icon: AlertTriangle, title: t('tiadaTunggakan'), description: t('tiadaTunggakanDesc') }}
          />
        </>
      )}
    </div>
  );
}
